/**
 * glowTextures — pre-rendered radial-gradient / ribbon textures, the
 * building block for the VFX-evolution arc (M1.S1). One soft white glow
 * sprite, tinted per use, powers fairy glow, fungi blobs, impact bloom,
 * god-ray ribbons, light halos — the "alive/lighting" feel ported from
 * djmsqrvve.com's CSS radial-gradients into Pixi.
 *
 * Split for testability:
 *   - GLOW_TEXTURE_SPECS  — pure config (stops, size). Unit-tested.
 *   - buildGlowCanvas     — browser raster (canvas radial gradient).
 *   - registerGlowTextures — boot step: raster → shared texture cache
 *                            straight from the canvas (registerCanvasTexture;
 *                            no PNG encode/decode), so any sprite
 *                            uses them by key. Called once at boot after
 *                            the Pixi factories register.
 *
 * Textures are WHITE-based (tinted at use via ISprite.setTint) so one
 * raster serves every color — keeps the GPU texture count tiny.
 */
import { registerCanvasTexture, hasSharedTexture } from './PixiSprite';
import { gameLogger } from '../../utils/GameLogger';

export type GlowTextureKey = 'glow_soft' | 'glow_tight' | 'glow_ring' | 'ray_ribbon' | 'vignette_mask' | 'sparkle';

export interface GlowStop {
  /** 0 (center) … 1 (edge). */
  offset: number;
  /** White base; tint at use. */
  alpha: number;
}

export interface GlowSpec {
  size: number;
  /** 'radial' = centered glow; 'ribbon' = vertical god-ray gradient;
   *  'sparkle' = soft radial core + 4 feathered diffraction spikes (twinkle). */
  kind: 'radial' | 'ribbon' | 'sparkle';
  stops: GlowStop[];
}

/**
 * Falloff curves ported from the djmsqrvve atmosphere CSS:
 *  - soft  : wide diffuse glow (fireflies, light blobs) — blur(8px) feel
 *  - tight : concentrated core (impact bloom, sparkles)
 *  - ring  : hollow center, bright rim (shockwave/aura rings)
 *  - ribbon: vertical god-ray shaft, soft top + bottom
 */
export const GLOW_TEXTURE_SPECS: Record<GlowTextureKey, GlowSpec> = {
  glow_soft: {
    size: 128,
    kind: 'radial',
    stops: [
      { offset: 0, alpha: 1 },
      { offset: 0.4, alpha: 0.5 },
      { offset: 1, alpha: 0 },
    ],
  },
  glow_tight: {
    size: 128,
    kind: 'radial',
    stops: [
      { offset: 0, alpha: 1 },
      { offset: 0.18, alpha: 0.85 },
      { offset: 0.5, alpha: 0.15 },
      { offset: 1, alpha: 0 },
    ],
  },
  glow_ring: {
    size: 128,
    kind: 'radial',
    stops: [
      { offset: 0, alpha: 0 },
      { offset: 0.55, alpha: 0.05 },
      { offset: 0.78, alpha: 1 },
      { offset: 0.92, alpha: 0.4 },
      { offset: 1, alpha: 0 },
    ],
  },
  ray_ribbon: {
    size: 128,
    kind: 'ribbon',
    stops: [
      { offset: 0, alpha: 0 },
      { offset: 0.2, alpha: 0.8 },
      { offset: 0.8, alpha: 0.8 },
      { offset: 1, alpha: 0 },
    ],
  },
  // INVERSE radial — clear center → opaque edge. Tinted dark + drawn over the
  // view with 'normal' blend it darkens the screen edges (a vignette frame).
  vignette_mask: {
    size: 256,
    kind: 'radial',
    stops: [
      { offset: 0, alpha: 0 },
      { offset: 0.5, alpha: 0 },
      { offset: 0.82, alpha: 0.45 },
      { offset: 1, alpha: 1 },
    ],
  },
  // Soft twinkle — a tight soft core (`stops`) plus four feathered axis spikes
  // (drawn additively). Reads as a gentle star-mote, not a hard star. For
  // magical fireflies / ice glints. Tinted at use like the others.
  sparkle: {
    size: 128,
    kind: 'sparkle',
    stops: [
      { offset: 0, alpha: 1 },
      { offset: 0.22, alpha: 0.55 },
      { offset: 0.6, alpha: 0.08 },
      { offset: 1, alpha: 0 },
    ],
  },
};

export const GLOW_TEXTURE_KEYS = Object.keys(GLOW_TEXTURE_SPECS) as GlowTextureKey[];

/** Raster one spec to a canvas (browser-only). White base, alpha-varying. */
export function buildGlowCanvas(spec: GlowSpec): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = spec.size;
  canvas.height = spec.size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  if (spec.kind === 'radial') {
    const r = spec.size / 2;
    const grad = ctx.createRadialGradient(r, r, 0, r, r, r);
    for (const s of spec.stops) grad.addColorStop(s.offset, `rgba(255,255,255,${s.alpha})`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, spec.size, spec.size);
  } else if (spec.kind === 'sparkle') {
    // Soft radial core from the stops…
    const r = spec.size / 2;
    const core = ctx.createRadialGradient(r, r, 0, r, r, r);
    for (const s of spec.stops) core.addColorStop(s.offset, `rgba(255,255,255,${s.alpha})`);
    ctx.fillStyle = core;
    ctx.fillRect(0, 0, spec.size, spec.size);
    // …plus four feathered axis spikes, blended additively so they read as a
    // gentle diffraction twinkle rather than hard lines.
    ctx.globalCompositeOperation = 'lighter';
    const spikeW = Math.max(2, spec.size * 0.06); // thin, soft
    const h = ctx.createLinearGradient(0, 0, spec.size, 0);
    h.addColorStop(0, 'rgba(255,255,255,0)');
    h.addColorStop(0.5, 'rgba(255,255,255,0.5)');
    h.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = h;
    ctx.fillRect(0, r - spikeW / 2, spec.size, spikeW);     // horizontal spike
    const v = ctx.createLinearGradient(0, 0, 0, spec.size);
    v.addColorStop(0, 'rgba(255,255,255,0)');
    v.addColorStop(0.5, 'rgba(255,255,255,0.5)');
    v.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = v;
    ctx.fillRect(r - spikeW / 2, 0, spikeW, spec.size);     // vertical spike
    ctx.globalCompositeOperation = 'source-over';
  } else {
    // Vertical ribbon: top→bottom linear gradient.
    const grad = ctx.createLinearGradient(0, 0, 0, spec.size);
    for (const s of spec.stops) grad.addColorStop(s.offset, `rgba(255,255,255,${s.alpha})`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, spec.size, spec.size);
  }
  return canvas;
}

export function glowTextureDataUrl(key: GlowTextureKey): string {
  return buildGlowCanvas(GLOW_TEXTURE_SPECS[key]).toDataURL('image/png');
}

let registered = false;
let readyResolve: (() => void) | null = null;
/** Resolves once the glow textures are registered (M3 atmosphere awaits this
 *  so it doesn't try to build sprites before the textures exist). */
export const glowTexturesReady: Promise<void> = new Promise((res) => { readyResolve = res; });

/**
 * Boot step — raster every glow texture and register it in the shared
 * Pixi texture cache by key. Idempotent; best-effort (a failure logs and
 * leaves effects to fall back to graphics primitives). Call once after
 * registerAllPixiFactories().
 */
export async function registerGlowTextures(): Promise<void> {
  if (registered) return;
  registered = true;
  try {
    for (const k of GLOW_TEXTURE_KEYS) {
      if (!hasSharedTexture(k)) registerCanvasTexture(k, buildGlowCanvas(GLOW_TEXTURE_SPECS[k]));
    }
    gameLogger.info('renderer', `[glowTextures] registered ${GLOW_TEXTURE_KEYS.length} glow textures`);
  } catch (e) {
    gameLogger.warn('renderer', `[glowTextures] registration failed: ${(e as Error)?.message ?? e}`);
  } finally {
    readyResolve?.();
    readyResolve = null;
  }
}

/** Test seam — reset the once-guard between specs. */
export function __resetGlowTexturesForTests(): void {
  registered = false;
}
